import React, { useEffect, memo, useMemo } from 'react';
import {
    StyledSelectedFilters,
    StyledSelected,
    StyledRemoveButton,
    StyledClearButton,
} from './styled';
import {
    connectCurrentRefinements,
    CurrentRefinementsProvided,
    RefinementValue,
} from 'react-instantsearch-core';
import Close from '$icons/close.svg';
import { useTranslation } from '~/shared/utils/translation';
import { weakKey } from '~/shared/utils/jsx';
import { useFranchise } from '~/shared/utils';
import { useFilterUIState } from '../../../hooks/useFilterUIState';
import { useFilterTracker } from './useFilterTracker';
import { usePrevious } from 'react-use';
import { removeAttributeDuplicates } from '~/features/productsList/utils';
import { useAlgoliaTracking } from '~/shared/hooks';

export const CurrentFilters = ({ items, refine }: CurrentRefinementsProvided) => {
    const { filtersCount, setFiltersCount, maxPrice, minPrice } = useFilterUIState(
        ({ filtersCount, setFiltersCount, maxPrice, minPrice }) => ({
            filtersCount,
            setFiltersCount,
            maxPrice,
            minPrice,
        })
    );
    const { translate } = useTranslation();
    const { activeMarket } = useFranchise();
    const previousCount = usePrevious<number>(filtersCount);
    const { trackFilters } = useFilterTracker();
    const { trackViewedAttributes } = useAlgoliaTracking();

    const handleFilterRemove = (value: RefinementValue) => {
        if (value) {
            refine(value);
        }
    };

    /*
     * Since we use multiple RefinementLists from algolia
     * the lib sends the selection for each one instanciated
     * the duplicates are removed and only one of each filter is shown
     * */
    const attributes = useMemo(() => removeAttributeDuplicates(items), [items]);

    const selectedFilters = useMemo(() => {
        const mappedItems: { label: string; value: RefinementValue }[] = [];

        attributes.forEach((filters) => {
            if (!filters) {
                return [];
            }

            if (filters.items) {
                filters.items.forEach((item) => {
                    mappedItems.push({
                        label: item.label,
                        value: item.value,
                    });
                });
            } else if (filters.currentRefinement && typeof filters.currentRefinement !== 'string') {
                const min =
                    filters.currentRefinement['min'] == undefined
                        ? minPrice
                        : filters.currentRefinement['min'];
                const max =
                    filters.currentRefinement['max'] == undefined
                        ? maxPrice
                        : filters.currentRefinement['max'];
                if (min !== undefined && max !== undefined) {
                    mappedItems.push({
                        label: `${min}-${max}${activeMarket?.currency?.toLocaleLowerCase()}`,
                        value: filters.value,
                    });
                }
            } else {
                mappedItems.push({
                    label: filters.currentRefinement,
                    value: filters.value,
                });
            }
        });

        return mappedItems;
    }, [activeMarket?.currency, attributes, maxPrice, minPrice]);

    useEffect(() => {
        setFiltersCount(selectedFilters.length);
    }, [selectedFilters.length, setFiltersCount]);

    useEffect(() => {
        // Because of so many rerenders..., we use count to avoid double tracking
        if (filtersCount !== previousCount) {
            trackFilters(items);
            trackViewedAttributes(attributes);
        }
    }, [items, filtersCount, previousCount, trackFilters, attributes, trackViewedAttributes]);

    return (
        <StyledSelectedFilters>
            {selectedFilters.map((filter) => {
                if (!filter) {
                    return null;
                }

                return (
                    <StyledSelected
                        key={weakKey(filter)}
                        onClick={() => handleFilterRemove(filter.value)}
                    >
                        {filter?.label}
                        <StyledRemoveButton
                            shape="icon"
                            size="sm"
                            variant="tertiary"
                            showHoverIndicator={false}
                        >
                            <Close title={translate('commerce.filters.removeFilter')} />
                        </StyledRemoveButton>
                    </StyledSelected>
                );
            })}
            {selectedFilters.length > 0 ? (
                <StyledClearButton
                    onClick={() => refine(items)}
                    disabled={!items.length}
                    size="sm"
                    variant="tertiary"
                >
                    {translate('commerce.filters.clearAll')}
                </StyledClearButton>
            ) : null}
        </StyledSelectedFilters>
    );
};

export const SelectedFilters = memo(connectCurrentRefinements(CurrentFilters));
